import type { FC } from 'react'
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'
import Alert from '@components/ui/alert'
import Button from '@components/ui/button'
import ProductCardAlpine from '@components/product/product-cards/product-card-alpine'
import ProductCardLoader from '@components/ui/loaders/product-card-loader'
import cn from 'classnames'
import { useProductsQuery } from '@framework/product/get-all-products'
import { LIMITS } from '@framework/utils/limits'
import { Product } from '@framework/types'
import { useState } from 'react'
import { ProductsConsumer } from '@contexts/products.context'

interface ProductGridProps {
  className?: string
  category?: string
  level?: number
}

export const ProductGrid: FC<ProductGridProps> = ({
  className = '',
  category,
  level,
}) => {
  const { t: translate } = useTranslation('common')
  const { query } = useRouter()
  const sort_by = typeof query.sort_by === 'string' ? query.sort_by : 'none'
  const [page, setPage] = useState(1)
  const filter = level && category ? `category_l${level}:="${category}"` : null
  const {
    isFetching: isLoading,
    isFetchingNextPage: loadingMore,
    fetchNextPage,
    hasNextPage,
    data,
    error,
  } = useProductsQuery({
    ...query,
    limit: LIMITS.PRODUCTS_LIMITS,
    category: category,
    level: level,
    page: page,
    sort: sort_by,
    filter,
  })

  const loadMore = () => {
    let next_page = page + 1
    setPage(next_page)
    fetchNextPage()
  }

  return (
    <>
      <div
        className={cn(
          'grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 3xl:grid-cols-6 gap-3 md:gap-4 2xl:gap-5',
          className,
        )}
      >
        {error ? (
          <div className="col-span-full">
            <Alert message={error?.message} />
          </div>
        ) : isLoading && !data?.pages?.length ? (
          Array.from({ length: 30 }).map((_, idx) => (
            <ProductCardLoader
              key={`product--key-${idx}`}
              uniqueKey={`product--key-${idx}`}
            />
          ))
        ) : (
          data?.pages?.map((page: any) => {
            return page?.data?.map((product: Product) => (
              <ProductCardAlpine
                key={`product--key-${product.id}`}
                product={product}
              />
            ))
          })
        )}
        {/* end of error state */}
      </div>
      {hasNextPage && (
        <div className="text-center pt-8 xl:pt-10">
          <ProductsConsumer>
            {(value) => (
              <>
                <p>
                  <Button
                    loading={loadingMore}
                    disabled={page > Math.floor(250 / LIMITS.PRODUCTS_LIMITS)}
                    onClick={() => loadMore()}
                    style={{
                      display:
                        page > Math.floor(250 / LIMITS.PRODUCTS_LIMITS) ||
                        parseInt(value.count?.replace(/\D/g, '')) <=
                          page * LIMITS.PRODUCTS_LIMITS
                          ? 'none'
                          : 'inline',
                    }}
                  >
                    {translate('button-load-more')}
                  </Button>
                </p>
                <p>
                  {' '}
                  {translate('text-showing')} 1 -{' '}
                  {Math.min(
                    page * LIMITS.PRODUCTS_LIMITS,
                    parseInt(value.count.replace(/\D/g, '')),
                  )}{' '}
                  {translate('text-of')} {value.count}{' '}
                  {translate('text-products')}
                </p>
                {page * LIMITS.PRODUCTS_LIMITS > 250 && (
                  <p>{translate('text-250-limit')}</p>
                )}
              </>
            )}
          </ProductsConsumer>
        </div>
      )}
    </>
  )
}
